import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Divider,
  Button,
  Collapse,
  Tooltip,
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Add as AddIcon, Delete as DeleteIcon, DragHandle as DragHandleIcon } from '@mui/icons-material';
import FieldEditor from './FieldEditor';

const SectionEditor = ({
  section,
  onUpdate,
  onDelete,
  index,
  isDragging = false,
  dragHandleProps = {},
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  
  const updateField = (fieldId, updatedField) => {
    const updatedFields = section.fields.map(field => 
      field.id === fieldId ? { ...field, ...updatedField } : field
    );
    onUpdate({
      ...section,
      fields: updatedFields
    });
  };

  const addField = () => {
    const newField = {
      id: `field-${Date.now()}`,
      type: 'text',
      label: 'New Field',
      required: false,
      options: []
    };
    onUpdate({
      ...section,
      fields: [...section.fields, newField]
    });
  };

  const deleteField = (fieldId) => {
    onUpdate({
      ...section,
      fields: section.fields.filter(field => field.id !== fieldId)
    });
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(section.fields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    onUpdate({
      ...section,
      fields: items
    });
  };

  return (
    <Paper
      elevation={isDragging ? 3 : 1}
      sx={{
        mb: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        opacity: isDragging ? 0.8 : 1,
      }}
    >
      <Box
        {...dragHandleProps}
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          backgroundColor: 'background.paper',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <DragHandleIcon sx={{ mr: 1, cursor: isDragging ? 'grabbing' : 'grab' }} />
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {section.title || 'Untitled Section'}
        </Typography>
        <Tooltip title="Delete Section">
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onDelete(section.id);
            }}
            color="error"
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={isExpanded}>
        <Box sx={{ p: 2, pt: 1 }}>
          <TextField
            fullWidth
            label="Section Title"
            value={section.title}
            onChange={(e) => onUpdate({ ...section, title: e.target.value })}
            margin="normal"
            size="small"
          />
          
          <TextField
            fullWidth
            label="Description (optional)"
            value={section.description || ''}
            onChange={(e) => onUpdate({ ...section, description: e.target.value })}
            margin="normal"
            size="small"
            multiline
            rows={2}
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" gutterBottom>
            Fields ({section.fields.length})
          </Typography>

          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId={`section-${section.id}`} type="FIELD">
              {(provided) => (
                <Box {...provided.droppableProps} ref={provided.innerRef}>
                  {section.fields.map((field, fieldIndex) => (
                    <Draggable
                      key={field.id}
                      draggableId={`field-${field.id}`}
                      index={fieldIndex}
                    >
                      {(provided, snapshot) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          mb={2}
                        >
                          <FieldEditor
                            field={field}
                            onUpdate={(updatedField) => updateField(field.id, updatedField)}
                            onDelete={deleteField}
                            index={fieldIndex}
                            isDragging={snapshot.isDragging}
                            dragHandleProps={provided.dragHandleProps}
                          />
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>

          <Button
            startIcon={<AddIcon />}
            onClick={addField}
            variant="outlined"
            size="small"
            sx={{ mt: 1 }}
          >
            Add Field
          </Button>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default SectionEditor;
